<template>
  <div class="active-chat">
    <div class="chat-header">
      <div class="d-flex align-items-center">
        <b-avatar size="36" text="C" class="mr-1" />
        <div>
          <h5 class="mb-0">
            {{ nomeContato }}
          </h5>
          <small class="text-muted">
            {{ conversa.protocolo }} · {{ rotuloEstado }}
            <span v-if="conversa.departamento">· {{ conversa.departamento.nome }}</span>
            <span v-if="conversa.unidade">· Unidade {{ conversa.unidade.codigo_cliente }} - {{ conversa.unidade.nome }}</span>
            <span v-if="conversa.user">· {{ conversa.user.name }}</span>
          </small>
        </div>
      </div>
      <div class="d-flex align-items-center">
        <b-button v-if="podeAssumir" size="sm" variant="primary" @click="$emit('assumir', conversa.id)">
          Assumir
        </b-button>
        <b-dropdown v-if="podeTransferir" size="sm" variant="outline-secondary" text="Transferir" class="ml-50">
          <b-dropdown-header>Departamento</b-dropdown-header>
          <b-dropdown-item v-for="dep in departamentos" :key="'dep-' + dep.id" @click="$emit('transferir', { atendimentoId: conversa.id, departamentoId: dep.id })">
            {{ dep.nome }}
          </b-dropdown-item>
          <template v-if="colegasDoDepartamento.length">
            <b-dropdown-divider />
            <b-dropdown-header>Operador</b-dropdown-header>
            <b-dropdown-item
              v-for="colega in colegasDoDepartamento"
              :key="'op-' + colega.id"
              @click="$emit('atribuir', { atendimentoId: conversa.id, userId: colega.id })"
            >
              {{ colega.name }}
            </b-dropdown-item>
          </template>
        </b-dropdown>
        <b-button v-if="podeEncerrar" size="sm" variant="outline-danger" class="ml-50" @click="$emit('encerrar', conversa.id)">
          Encerrar
        </b-button>
        <b-button v-if="podeReabrir" size="sm" variant="outline-secondary" class="ml-50" @click="$emit('reabrir', conversa.id)">
          Reabrir
        </b-button>
      </div>
    </div>

    <div ref="areaMensagens" class="user-chats">
      <h6 v-if="carregando" class="text-center text-muted mt-2">
        Carregando mensagens...
      </h6>
      <div v-else class="chats">
        <bolha-mensagem v-for="mensagem in mensagens" :key="mensagem.id" :mensagem="mensagem" />
      </div>
    </div>

    <compositor
      :desabilitado="!podeEnviar"
      @enviar="texto => $emit('enviar', texto)"
      @enviar-arquivo="payload => $emit('enviar-arquivo', payload)"
    />
    <small v-if="motivoBloqueio" class="text-danger px-1 pb-50 d-block">
      {{ motivoBloqueio }}
    </small>
  </div>
</template>

<script>
import { BAvatar, BButton, BDropdown, BDropdownItem, BDropdownHeader, BDropdownDivider } from 'bootstrap-vue'
import BolhaMensagem from './BolhaMensagem.vue'
import Compositor from './Compositor.vue'

export default {
  components: {
    BAvatar, BButton, BDropdown, BDropdownItem, BDropdownHeader, BDropdownDivider, BolhaMensagem, Compositor,
  },
  props: {
    conversa: { type: Object, required: true },
    mensagens: { type: Array, default: () => [] },
    carregando: { type: Boolean, default: false },
    departamentos: { type: Array, default: () => [] },
  },
  data() {
    return {
      colegasDoDepartamento: [],
    }
  },
  computed: {
    nomeContato() {
      const contato = this.conversa.contato || {}
      return contato.nome || contato.nome_perfil || contato.wa_id || 'Desconhecido'
    },
    rotuloEstado() {
      const mapa = {
        bot: 'Com a URA',
        fila: 'Na fila',
        em_atendimento: 'Em atendimento',
        encerrado: 'Encerrado',
      }
      return mapa[this.conversa.estado] || this.conversa.estado
    },
    meuId() {
      return this.$store.state.user.id
    },
    podeAssumir() {
      return this.conversa.estado === 'fila'
    },
    podeEncerrar() {
      return this.conversa.estado === 'em_atendimento' && this.conversa.user_id === this.meuId
    },
    // Diferente de encerrar (que so faz sentido pra quem ja segura o
    // ticket), transferir precisa funcionar mesmo achou "Com a URA" ou
    // "Na fila" - e o unico jeito de um admin/supervisor resgatar uma
    // conversa presa no fluxo automatico pra dentro de um departamento. A
    // autorizacao de verdade (quem pode mexer nesse atendimento) e do
    // backend; aqui so decide quando mostrar o botao.
    podeTransferir() {
      return this.conversa.estado !== 'encerrado'
    },
    podeReabrir() {
      return this.conversa.estado === 'encerrado' && this.$can('atendimento_reabrir')
    },
    janelaAberta() {
      if (!this.conversa.janela_expira_em) return false
      return new Date(this.conversa.janela_expira_em).getTime() > Date.now()
    },
    podeEnviar() {
      return this.conversa.estado === 'em_atendimento' && this.janelaAberta
    },
    motivoBloqueio() {
      if (this.conversa.estado === 'encerrado') return null // ja tem o botao Reabrir, nao precisa de aviso extra
      if (this.conversa.estado !== 'em_atendimento') return 'Assuma o atendimento antes de responder.'
      if (!this.janelaAberta) return 'Janela de 24h expirada - use um modelo de mensagem aprovado (ainda não implementado).'
      return null
    },
  },
  watch: {
    mensagens() {
      this.$nextTick(this.rolarParaBaixo)
    },
    'conversa.id': {
      immediate: true,
      handler() {
        this.$nextTick(this.rolarParaBaixo)
      },
    },
    'conversa.departamento_id': {
      immediate: true,
      handler(departamentoId) {
        this.carregarColegas(departamentoId)
      },
    },
  },
  methods: {
    rolarParaBaixo() {
      const el = this.$refs.areaMensagens
      if (el) el.scrollTop = el.scrollHeight
    },
    carregarColegas(departamentoId) {
      this.colegasDoDepartamento = []
      if (!departamentoId) return
      window.axios
        .get(this.$store.state.api + 'departamentos/' + departamentoId + '/operadores')
        .then(resposta => {
          this.colegasDoDepartamento = resposta.data.filter(u => u.id !== this.meuId)
        })
        .catch(() => {})
    },
  },
}
</script>

<style scoped>
/*
  Flexbox proprio, co-localizado com o template que ele afeta - em vez de
  depender do calc(100% - 65px - 65px) do parcial importado em Painel.vue
  (que assume cabecalho/rodape com exatamente 65px; o nosso cabecalho, com
  avatar + nome + protocolo/estado/departamento/operador + botoes, e mais
  alto que isso). O estilo scoped do Vue tambem carimba o elemento RAIZ do
  <compositor> (componente filho) com o mesmo atributo de escopo, entao
  ".chat-app-form" aqui alcança o compositor sem precisar de deep selector.
*/
.active-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.chat-header {
  flex: 0 0 auto;
  height: auto !important;
}

.user-chats {
  flex: 1 1 auto;
  min-height: 0;
  height: auto !important;
  overflow-y: auto;
}

.chat-app-form {
  flex: 0 0 auto;
  height: auto !important;
}
</style>
